import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, Image as ImageIcon, FileDown, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useCurrency } from "@/lib/CurrencyContext";
import { defaultBondRateForCurrency } from "@/lib/softCostsTemplates";
import {
  CONSTRUCTION_TYPES, ROOF_TYPES, FOUNDATION_TYPES, WALL_FINISHES,
} from "@/lib/constructionTypes";
import CostBreakdownChart from "@/components/CostBreakdownChart";
import ProfitWaterfall from "@/components/ProfitWaterfall";
import ProjectReportDialog from "@/components/ProjectReportDialog";
import { toast } from "sonner";

const MAX_PHOTO_DIM = 1600; // px — downscale longest edge
const PHOTO_JPEG_QUALITY = 0.85;
const MAX_PHOTO_BYTES = 25 * 1024 * 1024; // 25MB raw upload guard

// Compress an image File to a JPEG data URL bounded by MAX_PHOTO_DIM.
// Returns the compressed data URL (string) or rejects.
function compressImageToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error(`${file.name} isn't an image`));
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      reject(new Error(`${file.name} is too large (max 25MB)`));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error(`Could not decode ${file.name}`));
      img.onload = () => {
        const { width, height } = img;
        const scale = Math.min(1, MAX_PHOTO_DIM / Math.max(width, height));
        const w = Math.round(width * scale);
        const h = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try {
          const dataUrl = canvas.toDataURL("image/jpeg", PHOTO_JPEG_QUALITY);
          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "duplex", label: "Duplex" },
  { value: "other", label: "Other" },
];

const RENOVATION_TYPES = [
  { value: "light", label: "Light" },
  { value: "medium", label: "Medium" },
  { value: "heavy", label: "Heavy" },
  { value: "full", label: "Full Refurb" },
];

const STARTER_PHOTOS = [
  "https://images.unsplash.com/photo-1768820787189-b3fa0489ea79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwc3Vuc2V0fGVufDB8fHx8MTc3NjY3NTIxMnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1646936190308-6faef1ac893c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzY2NzUyMTJ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1600684388091-627109f3cd60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzY2NzUyMTJ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1742415106160-594d07f6cc23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50cyUyMGZsYXRsYXl8ZW58MHx8fHwxNzc2Njc1MjEyfDA&ixlib=rb-4.1.0&q=85",
];

export default function OverviewTab({ project, onChange }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const photos = project.photos?.length ? project.photos : [];
  const currency = useCurrency();
  const isSpaceCost = project.product === "spacecost";

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...photos];
    let okCount = 0;
    for (const f of files) {
      try {
        const dataUrl = await compressImageToDataUrl(f);
        next.push(dataUrl);
        okCount += 1;
      } catch (err) {
        toast.error(err?.message || `Could not add ${f.name}`);
      }
    }
    if (okCount > 0) {
      onChange({ photos: next });
      toast.success(`Added ${okCount} photo${okCount === 1 ? "" : "s"}`);
    }
    setUploading(false);
  };

  const onFileInputChange = (e) => {
    handleFiles(e.target.files);
    // Reset so picking the same file again still triggers onChange.
    e.target.value = "";
  };

  const removePhoto = (idx) => {
    const next = photos.slice();
    next.splice(idx, 1);
    onChange({ photos: next });
  };

  const prefillPhotos = () => {
    onChange({ photos: [...photos, ...STARTER_PHOTOS] });
  };

  return (
    <div className="space-y-6 fc-fade-in">
      {/* Visual insights — SpaceCost is budgeting only, no valuation/feasibility. */}
      {isSpaceCost ? (
        <section data-testid="insights-row-spacecost">
          <CostBreakdownChart project={project} />
        </section>
      ) : (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="insights-row">
          <CostBreakdownChart project={project} />
          <ProfitWaterfall project={project} />
        </section>
      )}

      {/* Project Summary Report (PDF) */}
      <section
        className="fc-card p-5 sm:p-6 flex items-center justify-between gap-4 flex-wrap"
        data-testid="report-card"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
            <FileDown className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="fc-heading text-[#1C1C1C] text-[15px]">
              {isSpaceCost ? "Room Budget Report" : "Project Summary Report"}
            </div>
            <div className="text-xs text-[#78716C] mt-0.5 max-w-md leading-relaxed">
              {isSpaceCost
                ? "Export a clean, share-ready PDF of this room budget — overview, category breakdown, and estimate-vs-actual variance. Print-ready for the contractor pack."
                : "Export a clean, share-ready PDF of this deal — overview, cost breakdown, ARV vs total cost, feasibility status, and an editable offer justification line you can take to agents and sellers."}
            </div>
          </div>
        </div>
        <button
          type="button"
          data-testid="generate-report-btn"
          onClick={() => setReportOpen(true)}
          className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3 text-sm font-medium flex items-center gap-1.5 transition-colors shrink-0"
        >
          <FileDown className="w-4 h-4" /> {isSpaceCost ? "Generate Room Budget Report (PDF)" : "Generate Project Report (PDF)"}
        </button>
      </section>

      <ProjectReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        project={project}
        onChange={onChange}
      />

      {/* Key Inputs */}
      <section className="fc-card p-6" data-testid="key-inputs-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="fc-heading text-lg text-[#1C1C1C]">Key Inputs</h2>
            <p className="text-xs text-[#A8A29E] mt-0.5">
              {isSpaceCost
                ? "Edit anything — totals recompute instantly."
                : "Edit anything — totals recompute instantly."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {!isSpaceCost && (
            <>
              <Field label={`Purchase Price (${currency})`}>
                <NumberInput
                  tid="purchase-price-input"
                  value={project.purchase_price}
                  onChange={(v) => onChange({ purchase_price: v })}
                  placeholder="650000"
                />
              </Field>
              <Field
                label={`Bond / Mortgage (${currency})`}
                hint="0 if cash buyer / owned outright. Drives bond interest & bond reg costs."
              >
                <NumberInput
                  tid="bond-amount-input"
                  value={project.bond_amount}
                  onChange={(v) => onChange({ bond_amount: v })}
                  placeholder={(project.purchase_price ? String(project.purchase_price) : "Same as purchase")}
                  allowEmpty
                />
              </Field>
              <Field
                label="Bond Interest Rate (% per year)"
                hint={`Annual rate. Default for ${currency}: ${defaultBondRateForCurrency(currency)}%.`}
              >
                <NumberInput
                  tid="bond-rate-input"
                  value={project.bond_rate}
                  onChange={(v) => onChange({ bond_rate: v })}
                  placeholder={String(defaultBondRateForCurrency(currency))}
                  allowEmpty
                />
              </Field>
              <Field
                label="Renovation Timeframe (months)"
                hint="How long you'll hold + renovate. Drives bond interest holding cost."
              >
                <NumberInput
                  tid="timeline-months-input"
                  value={project.timeline_months}
                  onChange={(v) => onChange({ timeline_months: v })}
                  placeholder="4"
                />
              </Field>
              <Field label={`Target Sale Price (${currency})`}>
                <NumberInput
                  tid="target-sale-price-input"
                  value={project.target_sale_price}
                  onChange={(v) => onChange({ target_sale_price: v })}
                  placeholder="850000"
                />
              </Field>
              <Field label="Floor Area (sqm)">
                <NumberInput
                  tid="floor-area-input"
                  value={project.floor_area_sqm}
                  onChange={(v) => onChange({ floor_area_sqm: v })}
                  placeholder="120"
                />
              </Field>
              <Field label="Property Type">
                <Select
                  value={project.property_type}
                  onValueChange={(v) => onChange({ property_type: v })}
                >
                  <SelectTrigger data-testid="property-type-select" className="fc-input h-10 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                    {PROPERTY_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Renovation Type">
                <Select
                  value={project.renovation_type}
                  onValueChange={(v) => onChange({ renovation_type: v })}
                >
                  <SelectTrigger data-testid="renovation-type-select" className="fc-input h-10 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                    {RENOVATION_TYPES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}
          {isSpaceCost && (
            <Field label="Type">
              <Select
                value={project.property_type || "house"}
                onValueChange={(v) => onChange({ property_type: v })}
              >
                <SelectTrigger data-testid="space-type-select" className="fc-input h-10 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
      </section>

      {/* Construction details — affects extension shell + exterior costs.
          SpaceCost shows them too so users can edit what they entered in the
          create wizard without re-typing it as floor area / type. */}
      <section className="fc-card p-6" data-testid="construction-card">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="fc-heading text-lg text-[#1C1C1C]">Construction Details</h2>
            <p className="text-xs text-[#A8A29E] mt-0.5">
              {isSpaceCost
                ? "Captured when you created this project. Edit anytime — these influence the rates we apply to room work."
                : <>Building style materially changes shell &amp; exterior costs. Updating these refreshes new items only — hit
                    <span className="fc-mono"> Refresh rates </span>
                    in the header to push the change through to existing items.</>}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Field label="Construction type">
            <Select
              value={project.construction_type || ""}
              onValueChange={(v) => onChange({ construction_type: v })}
            >
              <SelectTrigger data-testid="ov-construction-type-select" className="fc-input h-10 rounded-md">
                <SelectValue placeholder="Pick a type…" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                {CONSTRUCTION_TYPES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Roof type">
            <Select
              value={project.roof_type || ""}
              onValueChange={(v) => onChange({ roof_type: v })}
            >
              <SelectTrigger data-testid="ov-roof-type-select" className="fc-input h-10 rounded-md">
                <SelectValue placeholder="Pick a type…" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                {ROOF_TYPES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Foundation">
            <Select
              value={project.foundation_type || ""}
              onValueChange={(v) => onChange({ foundation_type: v })}
            >
              <SelectTrigger data-testid="ov-foundation-type-select" className="fc-input h-10 rounded-md">
                <SelectValue placeholder="Pick a type…" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                {FOUNDATION_TYPES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Wall finish">
            <Select
              value={project.wall_finish || ""}
              onValueChange={(v) => onChange({ wall_finish: v })}
            >
              <SelectTrigger data-testid="ov-wall-finish-select" className="fc-input h-10 rounded-md">
                <SelectValue placeholder="Pick a type…" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
                {WALL_FINISHES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      {/* Notes + Photos */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 fc-card p-6" data-testid="notes-card">
          <h2 className="fc-heading text-lg text-[#1C1C1C]">Notes</h2>
          <p className="text-xs text-[#A8A29E] mt-0.5 mb-4">Due diligence, agent feedback, quirks of the property.</p>
          <Textarea
            data-testid="project-notes-textarea"
            value={project.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="North-facing, needs roof re-spray, kitchen is original 90s..."
            className="fc-input min-h-[220px] text-sm leading-relaxed"
          />
        </div>

        <div className="lg:col-span-3 fc-card p-6" data-testid="photos-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="fc-heading text-lg text-[#1C1C1C]">Photos</h2>
              <p className="text-xs text-[#A8A29E] mt-0.5">Pick photos from your device — they're resized for fast loading.</p>
            </div>
            {photos.length === 0 && (
              <button
                data-testid="prefill-photos-btn"
                onClick={prefillPhotos}
                className="text-xs text-[#78716C] hover:text-[#1C1C1C] border border-black/[0.08] hover:border-black/[0.2] rounded-md px-2.5 py-1.5 transition-colors"
              >
                Use sample set
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onFileInputChange}
            className="hidden"
            data-testid="photo-file-input"
          />

          <div
            role="button"
            tabIndex={0}
            data-testid="photo-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFiles(e.dataTransfer?.files);
            }}
            className={`mb-4 cursor-pointer rounded-lg border border-dashed transition-colors px-4 py-5 text-center ${
              uploading
                ? "border-sage-300 bg-sage-50 text-sage-700"
                : "border-black/[0.12] hover:border-sage-300 hover:bg-sage-50 text-[#57534E]"
            }`}
          >
            <Upload className="w-5 h-5 mx-auto text-[#78716C]" />
            <div className="text-sm mt-2">
              {uploading ? "Processing photos…" : (
                <>
                  <span className="text-[#1C1C1C] font-medium">Click to browse</span> or drag & drop
                </>
              )}
            </div>
            <div className="text-[11px] text-[#A8A29E] mt-1">JPG / PNG · auto-resized to 1600px</div>
          </div>

          {photos.length === 0 ? (
            <div className="border border-dashed border-black/[0.08] rounded-lg py-10 text-center">
              <ImageIcon className="w-6 h-6 mx-auto text-[#A8A29E]" />
              <div className="text-xs text-[#A8A29E] mt-2">No photos yet</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="photos-grid">
              {photos.map((src, i) => (
                <div key={`${src}-${i}`} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-black/[0.06]">
                  <img src={src} alt="Property" className="w-full h-full object-cover" />
                  <button
                    data-testid={`remove-photo-${i}`}
                    onClick={() => removePhoto(i)}
                    className="absolute top-2 right-2 bg-[#1C1C1C]/70 text-[#1C1C1C] rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove photo"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="fc-label mb-1.5">{label}</div>
      {children}
      {hint && (
        <div className="text-[10px] text-[#A8A29E] mt-1 leading-snug">{hint}</div>
      )}
    </div>
  );
}

function NumberInput({ tid, value, onChange, placeholder, allowEmpty = false }) {
  return (
    <Input
      data-testid={tid}
      type="number"
      value={value === undefined || value === null ? (allowEmpty ? "" : 0) : value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" && allowEmpty) {
          onChange(null);
          return;
        }
        onChange(v === "" ? 0 : Number(v));
      }}
      placeholder={placeholder}
      className="fc-input h-10 rounded-md"
    />
  );
}
